import { kebabCase } from "lodash-es";
import { Descendant, Element, Node, Text } from "slate";

import { addSchemeToUrlIfEmpty } from "@mapmycustomers/shared";
import Resource from "@mapmycustomers/shared/types/entity/Resource";
import parsePhoneNumber from "@mapmycustomers/shared/util/phone/parsePhoneNumber";

import IConfigProviderContext from "../../../ConfigProvider/types/IConfigProviderContext";
import ElementType from "../../../type/slate/ElementType";
import TextSize from "../../../type/slate/TextSize";
import { cssAlignMap } from "../type/AlignElementType";
import ResourceGetter from "../type/ResourceGetter";

import {
  RESOURCE_ID_DATA_ATTRIBUTE,
  RESOURCE_LINK_ID_PREFIX,
  TEXT_SIZE_TO_FONT_SIZE,
} from "./const";
import { EMAIL_TEXT_REGEX } from "./parseEmailText";

export const RESOURCE_ID_DATA_ATTRIBUTE_NAME = kebabCase(RESOURCE_ID_DATA_ATTRIBUTE);

const getFontSizeStyle = (textSize: TextSize) => {
  return `font-size: ${TEXT_SIZE_TO_FONT_SIZE[textSize]}px`;
};

const serializeTextToHTML = (text: Text, element?: Element): string => {
  let result =
    element?.type === ElementType.LINK
      ? text.text
      : text.text.replace(EMAIL_TEXT_REGEX, (substring: string, ...args) => {
          const groups: NonNullable<RegExpMatchArray["groups"]> = args[args.length - 1];
          const url = groups["url"];
          const email = groups["email"];
          const phone = groups["phone"];
          if (url) {
            return `<a href="${addSchemeToUrlIfEmpty(url)}" target="_blank"><u>${url}</u></a>`;
          }
          if (email) {
            return `<a href="mailto:${email}" target="_blank"><u>${email}</u></a>`;
          }
          if (phone) {
            const parsedPhoneNumber = parsePhoneNumber(text.text);
            const phoneNumberLink = parsedPhoneNumber?.link;
            if (phoneNumberLink) {
              return `<a href="${phoneNumberLink}" target="_blank"><u>${phone}</u></a>`;
            }
          }
          return substring;
        });

  result = `<span style="${getFontSizeStyle(text.size ?? TextSize.MEDIUM)}">${result}</span>`;

  if (text.bold) {
    result = `<b>${result}</b>`;
  }

  if (text.italic) {
    result = `<em>${result}</em>`;
  }

  if (text.underline) {
    result = `<u>${result}</u>`;
  }

  if (text.strike) {
    result = `<span style="text-decoration: line-through">${result}</span>`;
  }

  if (text.color) {
    result = `<span style="color: ${text.color}">${result}</span>`;
  }

  return result;
};

const serializeResourceLinkToHTML = (
  resource: Resource,
  configProvider: IConfigProviderContext
) => {
  return `
    <div
      data-${RESOURCE_ID_DATA_ATTRIBUTE_NAME}="${resource.id}"
      class="resource-download-link"
      contenteditable="false"
      style="display: flex;max-width: 420px;"
    >
      <img
        width="12px"
        height="16px"
        src="https://mmc-mobile-public.s3.us-east-2.amazonaws.com/email-file-icon.png"
        style="margin-right: 5px;margin-top: 1px"
      />
      <span
        style="
          color: #3f4659;
          font-size: 1em;
          margin-right: 4px;
          vertical-align: middle;
          display: inline-block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        "
      >
        ${resource.name}
      </span>
      <a
        class="download-link"
        href="{${RESOURCE_LINK_ID_PREFIX}${resource.id}}"
        style="
          font-size: 1em;
          text-decoration: none;
          vertical-align: middle;
          margin-left: 12px;"
      >
        ${configProvider.formatMessage("ui.emailBody.resourceLink.downloadLinkText")}
      </a>
    </div>`;
};

const serializeElementToHTML = ({
  element,
  ...args
}: { element: Element } & Omit<SerializeToHTMLArgs, "descendants">): string => {
  let content = serializeToHTML({ descendants: element.children, ...args });

  let styleString = "";
  if (Element.isElement(element)) {
    if (element.align) {
      styleString += `text-align: ${cssAlignMap[element.align]};`;
    }
    if (element.indent) {
      styleString += `margin-left: ${element.indent}rem;`;
    }
    if (element.type === ElementType.BULLETED_LIST) {
      return `<ul style="${styleString}">${content}</ul>`;
    }
    if (element.type === ElementType.LIST_ITEM) {
      return `<li style="${styleString}">${content}</li>`;
    }
    if (element.type === ElementType.NUMBERED_LIST) {
      return `<ol style="${styleString}">${content}</ol>`;
    }
    if (element.type === ElementType.DYNAMIC_VARIABLE) {
      return `<span style="${getFontSizeStyle(TextSize.MEDIUM)}">${element.value}</span>`;
    }
    if (element.type === ElementType.IMAGE) {
      const img = `<img alt="${element.alt ?? ""}" src="${element.src}" ${
        element.width ? `width="${element.width}px"` : ""
      } />`;
      return element.url
        ? `<a href="${addSchemeToUrlIfEmpty(element.url)}" target="_blank">${img}</a>`
        : img;
    }
    if (element.type === ElementType.LINK) {
      return `<a href="${element.url}" target="_blank">${content}</a>`;
    }
    if (element.type === ElementType.RESOURCE_LINK && args.resourceGetter) {
      const resource = args.resourceGetter(element.resourceId);
      return resource ? serializeResourceLinkToHTML(resource, args.configProvider) : "";
    }
  }

  const hasImage = () => {
    for (const [descendant] of Node.descendants(element)) {
      if (Element.isElement(descendant) && descendant.type === ElementType.IMAGE) {
        return true;
      }
    }
  };

  if (!hasImage()) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    if (tempDiv.innerText === "") {
      content = "<br/>";
    }
  }

  return `<div${styleString ? ` style="${styleString}"` : ""}>${content}</div>`;
};

interface SerializeToHTMLArgs {
  configProvider: IConfigProviderContext;
  descendants: Descendant[];
  resourceGetter?: ResourceGetter;
}

const serializeToHTML = ({ descendants, ...args }: SerializeToHTMLArgs): string => {
  return descendants
    .map((descendant) => {
      if (Text.isText(descendant)) {
        return serializeTextToHTML(descendant);
      } else if (Element.isElement(descendant)) {
        return serializeElementToHTML({ element: descendant, ...args });
      }
      return "";
    })
    .join("");
};

export default serializeToHTML;
